import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, BarChart2 } from "lucide-react";
import { apiGet } from "../../Api/Client.js";
import { createLatestGate } from "../../Shared/latestRequest.js";
import "../../Styles/LmsAnalyticsScreen.css";
import { fmtCurrency, fmtNum, pct, stageColor } from "./crmFormat.js";
import { buildOwnerStageMatrix } from "./ownerStageMatrix.js";

// Split 2026-10-07 from the old combined "Analytics & Perf" screen — this half
// keeps the pipeline/deal-closure reporting tab (and its route). BA
// Performance moved out to SalesPerformanceScreen.jsx. See the matching split
// in Backend/EnterpriseCore/pages.py.

// ─── Helpers ──────────────────────────────────────────────────────────────────

// The pipeline in its natural left-to-right order, used to order the columns of
// the Assigned To × Stage table. Any stage the data carries that isn't listed
// here (e.g. the closed stages) sorts to the end rather than being dropped.
const STAGE_ORDER = [
  "New Lead",
  "Contact Attempted",
  "Engaged",
  "Discovery / Demo Scheduled",
  "Demo Done / Solution Fit",
  "Proposal Sent",
  "Nurture / Recycle",
  // Closed stages sit at the end (shown only via "Show Closed"), in the order
  // the founder reads them: Won, then Lost, then Cold.
  "Closed - Won",
  "Closed - Lost",
  "Closed - Cold",
];
const stageRank = (stage) => {
  const i = STAGE_ORDER.indexOf(stage);
  return i === -1 ? STAGE_ORDER.length : i;
};

const SOURCE_COLORS = {
  "Banao Website": "var(--blue-500)",
  LinkedIn: "#0077b5",
  Instagram: "#e1306c",
  Twitter: "#1da1f2",
  GitHub: "var(--c-900)",
  "Client Website": "var(--green-600)",
  InterviewGod: "var(--indigo-500)",
  Vikaas: "var(--red-600)",
  LMS: "var(--indigo-500)",
};

function sourceColor(src) {
  return SOURCE_COLORS[src] || "var(--c-500)";
}

// ─── Analytics & Pipeline Insights ──────────────────────────────────────────────

const CLOSED_STAGE_LABELS = new Set(["Closed - Won", "Closed - Lost", "Closed - Cold", "ClosedWon", "ClosedLost", "ClosedCold", "Closed Won", "Closed Lost", "Closed Cold"]);

function GroupTable({ title, rows, total, linkFor, navigate }) {
  if (!rows?.length) return null;
  return (
    <div className="LmsA-Chart-Card" style={{ flex: 1, minWidth: 220 }}>
      <div className="LmsA-Chart-Title">{title}</div>
      <table className="LmsA-Table" style={{ width: "100%" }}>
        <tbody>
          {rows.map((r) => {
            const href = linkFor ? linkFor(r) : null;
            return (
              <tr
                key={r.label}
                style={href ? { cursor: "pointer" } : undefined}
                title={href ? "Open In Sales CRM With This Filter" : undefined}
                onClick={href ? () => navigate?.(href) : undefined}
              >
                <td>{href ? <span style={{ color: "var(--blue-600)", fontWeight: 600 }}>{r.label}</span> : r.label}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{fmtNum(r.count)}</td>
                <td style={{ textAlign: "right", color: "var(--c-400)", fontSize: 12, width: 56 }}>{pct(r.count, total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AnalyticsSection({ data, days, setDays, navigate, company }) {
  const [showClosedStages, setShowClosedStages] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("All");
  const [productFilter, setProductFilter] = useState("All");
  const [srcPerfProduct, setSrcPerfProduct] = useState("All");
  if (!data) return <div className="LmsA-Loading">Loading Analytics…</div>;

  const windowLabel = days === 0 ? "All" : `${days} Days`;
  const g = data.groupings || {};
  // Stage distribution — filterable by source AND product together, from the
  // source × product × stage cross-tab. Ordered by the same pipeline sequence
  // as the matrix; closed stages hidden until "Show Closed" reveals them.
  const sps = data.source_product_stage || [];
  const sourceOptions = Array.from(new Set(sps.map((r) => r.source))).sort();
  const productOptions = Array.from(new Set(sps.map((r) => r.product))).sort();
  const stageCounts = {};
  for (const r of sps) {
    if (sourceFilter !== "All" && r.source !== sourceFilter) continue;
    if (productFilter !== "All" && r.product !== productFilter) continue;
    stageCounts[r.stage] = (stageCounts[r.stage] || 0) + r.count;
  }
  const stageRows = Object.entries(stageCounts)
    .filter(([stage]) => showClosedStages || !CLOSED_STAGE_LABELS.has(stage))
    .map(([stage, count]) => ({ stage, count }))
    .sort((a, b) => stageRank(a.stage) - stageRank(b.stage) || a.stage.localeCompare(b.stage));
  const stageTotalCount = stageRows.reduce((sum, r) => sum + r.count, 0);

  // Source performance — narrowable to a single product, aggregated by source
  // from the source × product performance cross-tab.
  const spp = data.source_product_perf || [];
  const srcPerfProductOptions = Array.from(new Set(spp.map((r) => r.product))).sort();
  const srcAgg = {};
  for (const r of spp) {
    if (srcPerfProduct !== "All" && r.product !== srcPerfProduct) continue;
    const a = srcAgg[r.source] || (srcAgg[r.source] = { source: r.source, total: 0, won: 0, value_won: 0 });
    a.total += r.total;
    a.won += r.won;
    a.value_won += Number(r.value_won || 0);
  }
  const srcPerfTotal = Object.values(srcAgg).reduce((sum, a) => sum + a.total, 0);
  const sourceRows = Object.values(srcAgg)
    .map((a) => ({ ...a, conversion_rate: a.total ? Math.round((a.won / a.total) * 1000) / 10 : 0 }))
    .sort((a, b) => b.total - a.total);

  // Every grouping row links to /lms/ with the equivalent URL filter.
  // window_start comes from the backend so both pages cut the window on the same date.
  const leadUrl = (params) => {
    const q = new URLSearchParams();
    if (g.window_start) q.set("created_after", g.window_start);
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") q.set(k, String(v)); });
    return `/lms/?${q.toString()}`;
  };

  // Owner × stage matrix: columns from the stages present, one row per owner (by id).
  const { stages: matrixStages, owners: matrixOwners, lookup: matrixLookup, stageTotals, grandTotal } =
    buildOwnerStageMatrix(g.owner_x_stage || [], stageRank);

  return (
    <div className="LmsA-Section">
      <div className="LmsA-Section-Header">
        <div className="LmsA-Section-Title"><BarChart2 size={18} /> Analytics & Pipeline Insights</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--c-500)" }}>Time Window:</span>
          <button className={`LmsA-Day-Pill ${days === 0 ? "active" : ""}`} onClick={() => setDays(0)}>All</button>
          {[30, 60, 90].map((d) => (
            <button key={d} className={`LmsA-Day-Pill ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Assigned To × Stage — the primary dashboard, kept first at the top by request. */}
      {matrixOwners.length > 0 && (
        <div className="LmsA-Sub-Section">
          <div className="LmsA-Sub-Title">Assigned To × Stage · {windowLabel} · {fmtNum(g.open_total || 0)} Open Leads</div>
          <div className="LmsA-Table-Wrap">
            <table className="LmsA-Table">
              <thead>
                <tr>
                  <th>Assigned To × Stage</th>
                  {matrixStages.map((s) => <th key={s} style={{ textAlign: "right" }}>{s}</th>)}
                  <th style={{ textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {matrixOwners.map((o) => (
                  <tr key={o.key}>
                    <td><strong style={o.active ? undefined : { color: "var(--red-500)" }} title={o.active ? undefined : "Not an active team member"}>{o.name}</strong></td>
                    {matrixStages.map((s) => {
                      const count = matrixLookup[`${o.key}|${s}`] || 0;
                      // Every cell opens the CRM with this filter, even a zero count —
                      // a blank cell must stay just as clickable as a filled one.
                      return (
                        <td
                          key={s}
                          style={{ textAlign: "right", cursor: "pointer", color: "var(--blue-600)", fontWeight: count ? 600 : undefined }}
                          title="Open In Sales CRM With This Filter"
                          onClick={() => navigate?.(leadUrl({ owner: o.id ?? "unassigned", stage: s }))}
                        >
                          {count || "–"}
                        </td>
                      );
                    })}
                    <td
                      style={{ textAlign: "right", cursor: "pointer", whiteSpace: "nowrap" }}
                      title="Open In Sales CRM With This Filter"
                      onClick={() => navigate?.(leadUrl({ owner: o.id ?? "unassigned" }))}
                    >
                      <span style={{ fontWeight: 700, color: "var(--blue-600)" }}>{fmtNum(o.total)}</span>
                      <span style={{ marginLeft: 10, color: "var(--c-500)", fontWeight: 600, fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{pct(o.total, grandTotal)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {/* The totals row was display-only — the last column (the grand
                    total) and every per-stage total above it had no link at all.
                    Each one now opens the CRM with the matching filter, same as
                    an ordinary cell. */}
                <tr className="LmsA-Total-Row">
                  <td><strong>Total</strong></td>
                  {matrixStages.map((s) => (
                    <td
                      key={s}
                      style={{ textAlign: "right", verticalAlign: "top", cursor: "pointer" }}
                      title="Open In Sales CRM With This Filter"
                      onClick={() => navigate?.(leadUrl({ stage: s }))}
                    >
                      <div style={{ fontWeight: 700, color: "var(--blue-600)" }}>{fmtNum(stageTotals[s])}</div>
                      <div style={{ color: "var(--c-500)", fontSize: 11.5, fontVariantNumeric: "tabular-nums" }}>{pct(stageTotals[s], grandTotal)}</div>
                    </td>
                  ))}
                  <td
                    style={{ textAlign: "right", verticalAlign: "top", whiteSpace: "nowrap", cursor: "pointer" }}
                    title="Open In Sales CRM With This Filter"
                    onClick={() => navigate?.(leadUrl({}))}
                  >
                    <div style={{ fontWeight: 800, color: "var(--blue-600)" }}>{fmtNum(grandTotal)}</div>
                    <div style={{ color: "var(--c-500)", fontSize: 11.5 }}>100%</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Pipeline Groupings — OPEN leads created in the selected window */}
      <div className="LmsA-Sub-Section">
        <div className="LmsA-Sub-Title">Open Pipeline Groupings · {windowLabel} · {fmtNum(g.open_total || 0)} Open Leads</div>
        <div className="LmsA-Charts-Row" style={{ flexWrap: "wrap", alignItems: "flex-start" }}>
          <GroupTable title="By Product" rows={g.by_product} total={g.open_total} navigate={navigate}
            linkFor={(r) => (r.label === "(blank)" ? null : leadUrl({ product: r.label }))} />
          <GroupTable title="By Channel" rows={g.by_channel} total={g.open_total} navigate={navigate}
            linkFor={(r) => (r.label === "(blank)" ? null : leadUrl({ channel: r.label }))} />
        </div>
      </div>

      {/* Source Performance — narrowable to a single product. */}
      <div className="LmsA-Sub-Section">
        <div className="LmsA-Sub-Title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span>Source Performance{srcPerfProduct !== "All" ? ` · ${srcPerfProduct}` : ""}</span>
          <select
            value={srcPerfProduct}
            onChange={(e) => setSrcPerfProduct(e.target.value)}
            style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid var(--c-200)", background: "var(--surface)", color: "var(--c-900)", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
          >
            <option value="All">All products</option>
            {srcPerfProductOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="LmsA-Table-Wrap">
          <table className="LmsA-Table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Total Leads</th>
                <th>Closed Won</th>
                <th>Conversion Rate</th>
                <th>Revenue Won</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {sourceRows.map((s, i) => (
                <tr
                  key={i}
                  style={{ cursor: "pointer" }}
                  title="Open In Sales CRM With This Filter"
                  onClick={() => navigate?.(leadUrl({ source: s.source, ...(srcPerfProduct !== "All" ? { product: srcPerfProduct } : {}) }))}
                >
                  <td>
                    <span className="LmsA-Source-Dot" style={{ background: sourceColor(s.source) }} />
                    <span style={{ color: "var(--blue-600)", fontWeight: 600 }}>{s.source || "Unknown"}</span>
                  </td>
                  <td><strong>{fmtNum(s.total)}</strong></td>
                  <td><span className="LmsA-Won-Badge">{s.won}</span></td>
                  <td>
                    <div className="LmsA-Conv-Bar">
                      <div className="LmsA-Conv-Fill" style={{ width: `${Math.min(100, s.conversion_rate)}%`, background: sourceColor(s.source) }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{s.conversion_rate}%</span>
                  </td>
                  <td>{fmtCurrency(s.value_won, company)}</td>
                  <td>
                    <div className="LmsA-Conv-Bar">
                      <div className="LmsA-Conv-Fill" style={{ width: pct(s.total, srcPerfTotal), background: "var(--indigo-500)" }} />
                    </div>
                    <span style={{ fontSize: 12 }}>{pct(s.total, srcPerfTotal)}</span>
                  </td>
                </tr>
              ))}
              {sourceRows.length === 0 && (
                <tr><td colSpan={6} style={{ color: "var(--c-500)" }}>No leads for this product.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stage Distribution — full-width table, pipeline order, filterable by source. */}
      <div className="LmsA-Sub-Section">
        <div className="LmsA-Sub-Title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span>Stage Distribution{sourceFilter !== "All" ? ` · ${sourceFilter}` : ""}{productFilter !== "All" ? ` · ${productFilter}` : ""} · {fmtNum(stageTotalCount)} Leads</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid var(--c-200)", background: "var(--surface)", color: "var(--c-900)", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
            >
              <option value="All">All sources</option>
              {sourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid var(--c-200)", background: "var(--surface)", color: "var(--c-900)", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
            >
              <option value="All">All products</option>
              {productOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button
              className={`LmsA-Day-Pill ${showClosedStages ? "active" : ""}`}
              style={{ fontSize: 11 }}
              onClick={() => setShowClosedStages((v) => !v)}
            >
              {showClosedStages ? "Hide Closed" : "Show Closed"}
            </button>
          </div>
        </div>
        <div className="LmsA-Table-Wrap">
          <table className="LmsA-Table">
            <thead>
              <tr>
                <th>Stage</th>
                <th style={{ textAlign: "right" }}>Leads</th>
                <th style={{ textAlign: "right" }}>Share</th>
              </tr>
            </thead>
            <tbody>
              {stageRows.map((r) => (
                <tr
                  key={r.stage}
                  style={{ cursor: "pointer" }}
                  title="Open In Sales CRM With This Filter"
                  onClick={() => navigate?.(leadUrl({
                    stage: r.stage,
                    ...(sourceFilter !== "All" ? { source: sourceFilter } : {}),
                    ...(productFilter !== "All" ? { product: productFilter } : {}),
                  }))}
                >
                  <td>
                    <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 999, marginRight: 8, background: stageColor(r.stage) }} />
                    <span style={{ color: "var(--blue-600)", fontWeight: 600 }}>{r.stage}</span>
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--c-900)" }}>{fmtNum(r.count)}</td>
                  <td style={{ textAlign: "right", color: "var(--c-500)", fontVariantNumeric: "tabular-nums" }}>{pct(r.count, stageTotalCount)}</td>
                </tr>
              ))}
              {stageRows.length === 0 && (
                <tr><td colSpan={3} style={{ color: "var(--c-500)" }}>No leads for this source.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="LmsA-Total-Row">
                <td><strong>Total</strong></td>
                <td style={{ textAlign: "right", fontWeight: 800, color: "var(--c-900)" }}>{fmtNum(stageTotalCount)}</td>
                <td style={{ textAlign: "right", color: "var(--c-500)" }}>100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Screen ───────────────────────────────────────────────────────────────

export function SalesPipelineScreen({ navigate, data: appData }) {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [days, setDays] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Clicking 30d then 90d quickly can be answered out of order; only the newest request may land.
  const gate = useMemo(createLatestGate, []);
  const fetchAll = useCallback(async () => {
    const ticket = gate.start();
    setLoading(true);
    setError("");
    try {
      const analytics = await apiGet(`/Lms/api/analytics/?days=${days}`);
      if (gate.isCurrent(ticket)) setAnalyticsData(analytics);
    } catch (e) {
      if (gate.isCurrent(ticket)) setError("Failed to load analytics data.");
    } finally {
      if (gate.isCurrent(ticket)) setLoading(false);
    }
  }, [days, gate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="LmsA-Screen">
      {error && (
        <div className="LmsA-Error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}
      <div className="LmsA-Content">
        <AnalyticsSection data={analyticsData} days={days} setDays={(d) => setDays(d)} navigate={navigate} company={appData?.me?.company} />
      </div>
    </div>
  );
}
